import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
  Switch,
  FormControl,
  InputLabel,
  FormControlLabel,
  Grid,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    maxWidth: 900,
  },
  entry: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    background: "inherit",
  },
  keyField: {
    minWidth: 160,
  },
  smallInput: {
    minWidth: 140,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  nested: {
    marginLeft: theme.spacing(3),
    marginTop: theme.spacing(1),
    borderLeft: "2px dashed currentColor",
    paddingLeft: theme.spacing(2),
  },
  addRow: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

function NodeEditor({ nodeObj, onNodeChange, path = [] }) {
  const classes = useStyles();
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    const normalized = Object.keys(nodeObj || {}).map((k) => {
      const v = nodeObj[k];
      let t;
      if (Array.isArray(v)) t = "array";
      else if (v === null) t = "string";
      else if (typeof v === "string") t = v.includes("\n") ? "text" : "string";
      else if (typeof v === "object") t = "object";
      else t = typeof v;

      let raw;
      if (t === "object" || t === "array") raw = v;
      else if (t === "boolean") raw = !!v;
      else raw = String(v ?? "");

      return {
        key: k,
        type: t === "undefined" ? "string" : t,
        value: t === "object" || t === "array" ? v : undefined,
        raw,
      };
    });
    setEntries(normalized);
  }, [nodeObj]);

  function pushChange(newEntries) {
    setEntries(newEntries);
    const out = {};
    for (const e of newEntries) {
      if (!e.key) continue;
      if (e.type === "object" || e.type === "array") {
        out[e.key] = e.value || (e.type === "array" ? [] : {});
      } else if (e.type === "number") {
        const n = parseFloat(e.raw);
        out[e.key] = Number.isFinite(n) ? n : 0;
      } else if (e.type === "boolean") {
        out[e.key] = !!e.raw;
      } else {
        out[e.key] = String(e.raw ?? "");
      }
    }
    onNodeChange(out);
  }

  function handleKeyChange(idx, newKey) {
    const copy = entries.slice();
    copy[idx] = { ...copy[idx], key: newKey };
    setEntries(copy);
    if (newKey.trim() !== "") pushChange(copy);
  }

  function handleTypeChange(idx, newType) {
    const copy = entries.slice();
    const prev = copy[idx];
    let nextValue;
    if (newType === "object") nextValue = prev.value || {};
    else if (newType === "array") nextValue = Array.isArray(prev.value) ? prev.value : [];
    else if (newType === "number") nextValue = 0;
    else if (newType === "boolean") nextValue = !!prev.raw;
    else nextValue = String(prev.raw ?? "");

    copy[idx] = { ...prev, type: newType, value: nextValue, raw: nextValue };
    pushChange(copy);
  }

  function handleRawChange(idx, newRaw) {
    const copy = entries.slice();
    copy[idx] = { ...copy[idx], raw: newRaw };
    pushChange(copy);
  }

  function handleBooleanToggle(idx) {
    const copy = entries.slice();
    copy[idx] = { ...copy[idx], raw: !copy[idx].raw };
    pushChange(copy);
  }

  function handleNestedChange(idx, newObj) {
    const copy = entries.slice();
    copy[idx] = { ...copy[idx], value: newObj };
    pushChange(copy);
  }

  function handleArrayChange(idx, newArr) {
    const copy = entries.slice();
    copy[idx] = { ...copy[idx], value: newArr };
    pushChange(copy);
  }

  function addEntry() {
    const copy = entries.slice();
    let base = i18n.t("jsonEditor.defaultKey", "key");
    let i = 1;
    while (copy.find((e) => e.key === base + (i === 1 ? "" : i))) i++;
    const key = base + (i === 1 ? "" : i);
    copy.push({ key, type: "string", value: "", raw: "" });
    pushChange(copy);
  }

  function removeEntry(idx) {
    const copy = entries.slice();
    copy.splice(idx, 1);
    pushChange(copy);
  }

  function handleMultilineKeyDown(ev, idx) {
    if (ev.key === "Enter") {
      if (ev.shiftKey) {
        const copy = entries.slice();
        if (entries[idx].type === "string") {
          copy[idx] = { ...entries[idx], type: "text", raw: entries[idx].raw + "\n" };
          pushChange(copy);
          ev.preventDefault();
        }
      } else {
        ev.preventDefault();
      }
    }
  }

  return (
    <div>
      {entries.map((e, idx) => (
        <Paper key={`${idx}`} className={classes.entry} elevation={0}>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <TextField
                className={classes.keyField}
                label={i18n.t("jsonEditor.key", "key")}
                value={e.key}
                onChange={(ev) => handleKeyChange(idx, ev.target.value)}
                size="small"
              />
            </Grid>

            <Grid item>
              <FormControl size="small">
                <InputLabel id={`type-label-${idx}`}>{i18n.t("jsonEditor.type", "type")}</InputLabel>
                <Select
                  labelId={`type-label-${idx}`}
                  value={e.type}
                  onChange={(ev) => handleTypeChange(idx, ev.target.value)}
                >
                  <MenuItem value="string">{i18n.t("jsonEditor.typeValues.string", "string")}</MenuItem>
                  <MenuItem value="text">{i18n.t("jsonEditor.typeValues.text", "text")}</MenuItem>
                  <MenuItem value="number">{i18n.t("jsonEditor.typeValues.number", "number")}</MenuItem>
                  <MenuItem value="boolean">{i18n.t("jsonEditor.typeValues.boolean", "boolean")}</MenuItem>
                  <MenuItem value="object">{i18n.t("jsonEditor.typeValues.object", "object")}</MenuItem>
                  <MenuItem value="array">{i18n.t("jsonEditor.typeValules.array", "array")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item>
              {e.type === "string" && (
                <TextField
                  className={classes.smallInput}
                  size="small"
                  label={i18n.t("jsonEditor.value", "value")}
                  value={e.raw}
                  onChange={(ev) => handleRawChange(idx, ev.target.value)}
                  onKeyDown={(ev) => handleMultilineKeyDown(ev, idx)}
                />
              )}

              {e.type === "text" && (
                <TextField
                  className={classes.smallInput}
                  size="small"
                  label={i18n.t("jsonEditor.value", "value")}
                  value={e.raw}
                  onChange={(ev) => handleRawChange(idx, ev.target.value)}
                  multiline
                  rows={4}
                  onKeyDown={(ev) => handleMultilineKeyDown(ev, idx)}
                />
              )}

              {e.type === "number" && (
                <TextField
                  className={classes.smallInput}
                  size="small"
                  label={i18n.t("jsonEditor.value", "value")}
                  value={e.raw}
                  onChange={(ev) => handleRawChange(idx, ev.target.value)}
                  type="number"
                />
              )}

              {e.type === "boolean" && (
                <FormControlLabel
                  control={<Switch checked={!!e.raw} onChange={() => handleBooleanToggle(idx)} />}
                  label={e.raw ? i18n.t("jsonEditor.true", "true") : i18n.t("jsonEditor.false", "false")}
                />
              )}

              {e.type === "object" && (
                <div className={classes.nested}>
                  <Typography variant="body2">{i18n.t("jsonEditor.objectLabel", "Object")}: {e.key}</Typography>
                  <NodeEditor
                    nodeObj={e.value || {}}
                    onNodeChange={(obj) => handleNestedChange(idx, obj)}
                    path={[...path, e.key]}
                  />
                </div>
              )}

              {e.type === "array" && (
                <div className={classes.nested}>
                  <Typography variant="body2">{i18n.t("jsonEditor.arrayLabel", "Array")}: {e.key}</Typography>
                  <ArrayEditor
                    arr={Array.isArray(e.value) ? e.value : []}
                    onArrayChange={(arr) => handleArrayChange(idx, arr)}
                  />
                </div>
              )}
            </Grid>

            <Grid item className={classes.actions}>
              <IconButton size="small" onClick={() => removeEntry(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        </Paper>
      ))}

      <div className={classes.addRow}>
        <Button size="small" startIcon={<AddIcon />} onClick={addEntry}>
          { i18n.t("jsonEditor.addEntry", "Add entry") }
        </Button>
      </div>
    </div>
  );
}

function ArrayEditor({ arr, onArrayChange }) {
  const classes = useStyles();

  function handleItemChange(idx, newVal) {
    const copy = arr.slice();
    copy[idx] = newVal;
    onArrayChange(copy);
  }

  function addItem() {
    onArrayChange([...arr, ""]);
  }

  function removeItem(idx) {
    const copy = arr.slice();
    copy.splice(idx, 1);
    onArrayChange(copy);
  }

  return (
    <div>
      {arr.map((val, idx) => (
        <Paper key={idx} className={classes.entry} elevation={0}>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <TextField
                className={classes.smallInput}
                size="small"
                label={i18n.t("jsonEditor.itemLabel", "item") + ` ${idx}`}
                value={String(val)}
                onChange={(e) => handleItemChange(idx, e.target.value)}
              />
            </Grid>
            <Grid item className={classes.actions}>
              <IconButton size="small" onClick={() => removeItem(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        </Paper>
      ))}
      <div className={classes.addRow}>
        <Button size="small" startIcon={<AddIcon />} onClick={addItem}>
          { i18n.t("jsonEditor.addItem", "Add item") }
        </Button>
      </div>
    </div>
  );
}

export default function JsonVisualEditor({ title, value = {}, onChange = () => {} }) {
  const classes = useStyles();
  const [root, setRoot] = useState(() => (value && typeof value === "object" ? value : {}));

  useEffect(() => {
    setRoot(value || {});
  }, [value]);

  function handleRootChange(newObj) {
    setRoot(newObj);
    onChange(newObj);
  }

  return (
    <>
      <div className={classes.header}>
        <Typography variant="body1">{ title || i18n.t("jsonEditor.title", "JSON Visual Editor") }</Typography>
      </div>

      <Box mt={2}>
        <NodeEditor nodeObj={root} onNodeChange={handleRootChange} />
      </Box>
    </>
  );
}
